import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';

import { useI18n } from '../../i18n/I18nProvider';

const ArtisanCommandCenter = ({ profile = {} }) => {
  const { language } = useI18n();
  const en = language === 'en';
  const template = profile.document_template || {};
  const missing = [
    !profile.profile_image && (en ? 'profile photo' : 'photo'),
    !profile.business_intro && (en ? 'business introduction' : 'présentation'),
    !(template.siret || profile.siret) && 'SIRET',
    !(template.professional_insurance || profile.professional_insurance || profile.insurance) && (en ? 'insurance' : 'assurance'),
    !template.payment_terms && (en ? 'payment terms' : 'conditions de paiement'),
  ].filter(Boolean);

  const cards = en ? [
    ['/newjobs', ExploreOutlinedIcon, 'Find local jobs', 'Relevant requests, photos and distance before you answer.', 'Explore'],
    ['/jobs', AssignmentTurnedInOutlinedIcon, 'Manage my jobs', 'Visits, quotes, active work and pending actions.', 'Open'],
    ['/planning', CalendarMonthOutlinedIcon, 'Organise my week', 'Appointments, tasks, deadlines and payment-ready jobs.', 'Plan'],
    ['/profileartisan', DescriptionOutlinedIcon, 'Prepare my quotes', 'Reuse your legal, VAT, insurance and payment information.', 'Set up'],
    ['/boutique', StorefrontOutlinedIcon, 'Grow my portfolio', 'Verified projects and clear before-and-after evidence.', 'Improve'],
  ] : [
    ['/newjobs', ExploreOutlinedIcon, 'Trouver des chantiers', 'Demandes locales pertinentes, photos et distance avant de répondre.', 'Explorer'],
    ['/jobs', AssignmentTurnedInOutlinedIcon, 'Gérer mes projets', 'Visites, devis envoyés, travaux actifs et actions en attente.', 'Ouvrir'],
    ['/planning', CalendarMonthOutlinedIcon, 'Organiser ma semaine', 'Rendez-vous, tâches, échéances et paiements prêts à démarrer.', 'Planifier'],
    ['/profileartisan', DescriptionOutlinedIcon, 'Préparer mes devis', 'Informations légales, TVA, assurance et conditions préremplies.', 'Configurer'],
    ['/boutique', StorefrontOutlinedIcon, 'Développer ma vitrine', 'Portfolio, réalisations vérifiées et preuves avant/après.', 'Améliorer'],
  ];

  return (
    <Section aria-label={en ? 'Artisan dashboard' : 'Tableau de bord artisan'}>
      <Header>
        <div>
          <Eyebrow>{en ? 'Your business, without the mental load' : 'Votre activité, sans charge mentale'}</Eyebrow>
          <h2>{en ? 'Run your business from one calm workspace' : 'Pilotez votre entreprise depuis un seul endroit'}</h2>
          <p>{en ? 'Opportunities, quotes, jobs, payments and your portfolio remain connected to the same project.' : 'Opportunités, devis, chantiers, paiements et vitrine restent reliés au même dossier projet.'}</p>
        </div>
        <Readiness $ready={!missing.length}>
          {missing.length
            ? (en ? `${missing.length} item${missing.length > 1 ? 's' : ''} to complete` : `${missing.length} élément${missing.length > 1 ? 's' : ''} à compléter`)
            : (en ? 'Profile ready to convert' : 'Profil prêt à convertir')}
        </Readiness>
      </Header>
      <Grid>
        {cards.map(([to, Icon, title, description, action]) => (
          <Card key={to} to={to}><Icon /><div><strong>{title}</strong><span>{description}</span></div><b>{action}</b></Card>
        ))}
      </Grid>
      {missing.length > 0 && (
        <Notice><strong>{en ? 'To build trust:' : 'Pour inspirer confiance :'}</strong> {en ? `complete ${missing.join(', ')}. This information will be reused in your quotes to save time.` : `complétez ${missing.join(', ')}. Ces informations seront réutilisées dans vos devis afin de vous faire gagner du temps.`}</Notice>
      )}
    </Section>
  );
};

const Section = styled.section`display:grid;gap:.9rem;padding:clamp(.9rem,2vw,1.15rem);border:1px solid #e4ded5;border-radius:1rem;background:linear-gradient(135deg,#fff 0%,#faf6ef 100%);`;
const Header = styled.header`display:flex;justify-content:space-between;gap:1rem;align-items:flex-start;h2{margin:.15rem 0;font-size:clamp(1.15rem,3vw,1.55rem);}p{margin:0;color:#6e6962;font-size:.78rem;line-height:1.5;}@media(max-width:40rem){flex-direction:column;}`;
const Eyebrow = styled.span`color:#92713f;font-size:.66rem;font-weight:900;text-transform:uppercase;letter-spacing:.09em;`;
const Readiness = styled.span`padding:.42rem .65rem;border-radius:999px;background:${({$ready})=>$ready?'#e7f7ed':'#fff1d7'};color:${({$ready})=>$ready?'#17623a':'#74500b'};font-size:.68rem;font-weight:850;white-space:nowrap;`;
const Grid = styled.div`display:grid;grid-template-columns:repeat(auto-fit,minmax(12.5rem,1fr));gap:.65rem;`;
const Card = styled(Link)`display:grid;grid-template-columns:auto minmax(0,1fr);gap:.5rem;padding:.8rem;border:1px solid #e4ded5;border-radius:.85rem;background:#fff;color:#111;text-decoration:none;box-shadow:0 .35rem 1rem rgba(0,0,0,.025);svg{font-size:1.15rem;color:#9d7b45;}div{display:grid;gap:.12rem;}strong{font-size:.76rem;}span{color:#746f68;font-size:.65rem;line-height:1.4;}b{grid-column:2;color:#7b5a27;font-size:.65rem;}`;
const Notice = styled.p`margin:0;padding:.7rem .8rem;border-radius:.75rem;background:#fff7e7;color:#65501f;font-size:.7rem;line-height:1.5;`;
export default ArtisanCommandCenter;
